/**
 * The two charts above an item list: how old the open work is, and how long
 * the closed work took (design: "Led AI Console.dc.html", the ISSUES block's
 * `showAging` pair).
 *
 * ## A pure function over rows the page already has, and no new request
 *
 * `/api/items` returns every row in scope — the list pages its RENDERING
 * (`usePagedList`), it does not page the read — and `ItemList` narrows those
 * rows to the saved view and project itself. So the exact set the table shows
 * is already in hand, and the charts are a fold over it.
 *
 * That is not just cheaper than a server aggregate, it is more correct. A
 * chart fed by its own query could disagree with the table beneath it — a
 * different clock, a different filter, a row that landed between the two
 * requests — and the reader would have no way to tell which number was
 * wrong. Folding the rendered rows makes disagreement impossible.
 *
 * It therefore follows the filters, which is what the design does too
 * (`scoped = byTab.filter(passes)`). A chart above a narrowed list that
 * described the un-narrowed scope would answer a question nobody asked.
 *
 * ## Age is days since CREATION
 *
 * How long the thing has existed, not how long since anyone last touched it
 * (user, 2026-09-11). The card says "open, by age" and that is what age
 * means: an item raised three weeks ago and nudged yesterday is three weeks
 * old, and a chart that filed it under "0–3d" would be answering a question
 * about activity while claiming to answer one about age.
 *
 * This is deliberately NOT the number in the Age column beside it, which is
 * `daysSinceMovement` — the one `computeStall` judges and the Stalled filter
 * acts on. The two measure different things and a reader should expect them
 * to differ: the bands say how long the backlog has existed, the column says
 * how long it has been ignored.
 *
 * ## The close time is `lastMovementAt`, and that is not a guess
 *
 * There is no `closedAt` on three of the four record kinds (`Action` alone
 * has `completedAt`). What there is: `lastMovementAt`, maintained by
 * `recordItemEvent`, and `computeStall`'s rule that a terminal item never
 * stalls — because nothing keeps touching a closed record. For an item whose
 * status is terminal, the last movement IS the close.
 *
 * The alternative was the `ItemEvent` log's `closed` event. It is no more
 * precise: `backfillItemEvents` synthesized historical events from
 * `updatedAt`, which is what `lastMovementAt` already holds, so the log would
 * buy a query and a join for the same numbers. If a real `closedAt` is ever
 * added, this is the one function that has to change.
 *
 * The honest limit, stated because the card prints a median somebody may
 * quote: an item edited AFTER being closed reports a longer close than it
 * took. Nothing in the console edits closed records today.
 */

import type { ConsoleItemKind, ConsoleItemStatus, ConsoleRow } from "../api";

/** Statuses that mean the work is done — `consoleItem`'s own terminal pair,
 *  named here because the client cannot import the server's set. */
const TERMINAL: ReadonlySet<ConsoleItemStatus> = new Set<ConsoleItemStatus>(["closed", "logged"]);

/** How far back the close chart looks. The design's own window. */
export const CLOSE_WINDOW_DAYS = 30;

/** The line the aging callout speaks about. The design's sentence ("have sat
 *  more than 7 days") names it, so it is a constant rather than a literal in
 *  two places. */
export const AGING_CALLOUT_DAYS = 7;

const MS_PER_DAY = 86_400_000;

/**
 * The open-age bands, in order, as `[label, lowest, highest]` inclusive days.
 *
 * Five, not four: the design splits 8–14 from 15–30 because a fortnight and a
 * month are different conversations with a contractor.
 */
export const AGE_BANDS: ReadonlyArray<readonly [string, number, number]> = [
  ["0–3d", 0, 3],
  ["4–7d", 4, 7],
  ["8–14d", 8, 14],
  ["15–30d", 15, 30],
  ["30d+", 31, Number.POSITIVE_INFINITY],
];

/** The time-to-close buckets, same shape. `< 1 day` covers 0 and 1 so a
 *  same-day close is not rounded into the next bucket. */
export const CLOSE_BUCKETS: ReadonlyArray<readonly [string, number, number]> = [
  ["< 1 day", 0, 1],
  ["1–7 days", 2, 7],
  ["1–4 weeks", 8, 30],
  ["> 1 Month", 31, Number.POSITIVE_INFINITY],
];

export interface ItemFlowBucket {
  label: string;
  count: number;
}

export interface ItemFlow {
  /** Non-terminal rows in scope — what the bands add up to. */
  openCount: number;
  bands: ItemFlowBucket[];
  /** The age in days of the oldest open row, or 0 when nothing is open. */
  oldestDays: number;
  /** Rows sitting longer than `AGING_CALLOUT_DAYS`. `pct` is of `openCount`,
   *  rounded, and is 0 rather than NaN when nothing is open. */
  overLine: { count: number; pct: number };
  /** `null` when nothing in scope closed inside the window — which the card
   *  says, rather than printing a median of no numbers. */
  closed: ItemFlowClosed | null;
}

export interface ItemFlowClosed {
  count: number;
  buckets: ItemFlowBucket[];
  /** The middle close time in days. A median, not a mean: one record that sat
   *  eleven months would drag an average somewhere no record actually is. */
  medianDays: number;
  withinWeekPct: number;
  /** The owner with the lowest median close, and that median — the design's
   *  "GC · 2d". `null` when no closed row in the window names an owner. */
  fastest: { owner: string; days: number } | null;
}

/** Which band a day count falls in. Returns the last band for anything above
 *  its floor, so there is no gap above the table. */
function bandIndexOf(days: number): number {
  const found = AGE_BANDS.findIndex(([, lo, hi]) => days >= lo && days <= hi);
  return found === -1 ? AGE_BANDS.length - 1 : found;
}

function bucketIndexOf(days: number): number {
  const found = CLOSE_BUCKETS.findIndex(([, lo, hi]) => days >= lo && days <= hi);
  return found === -1 ? CLOSE_BUCKETS.length - 1 : found;
}

/**
 * The middle value of a sorted list — the UPPER middle when the count is
 * even.
 *
 * Not an average of the two middles: every number these cards print is meant
 * to be a close somebody can actually point at. And not the lower middle,
 * which is how this first shipped and was wrong: an owner with closes of 0
 * and 40 days reported 0 and took the "fastest owner" title off someone who
 * closes everything in three. Where the answer has to lean, a figure claiming
 * somebody is fast should lean slow.
 */
function medianOf(sorted: number[]): number {
  return sorted[Math.floor(sorted.length / 2)] ?? 0;
}

function pctOf(part: number, whole: number): number {
  return whole === 0 ? 0 : Math.round((part / whole) * 100);
}

/**
 * How many whole days old the row is.
 *
 * Floored, because a band has integer edges: an item 3.4 days old belongs in
 * 0–3d rather than falling between that band and the next. An unparseable
 * `createdAt` counts as brand new rather than as infinitely old — a bad stamp
 * should not invent a backlog.
 */
function ageDays(row: ConsoleRow, now: Date): number {
  const born = Date.parse(row.createdAt);
  if (!Number.isFinite(born)) return 0;
  return Math.max(0, Math.floor((now.getTime() - born) / MS_PER_DAY));
}

/** Days from creation to the close, floored — see the header on the source.
 *  The wire carries both stamps as ISO strings. */
function closeDaysOf(row: ConsoleRow): number {
  const took = Date.parse(row.lastMovementAt) - Date.parse(row.createdAt);
  return Number.isFinite(took) ? Math.max(0, Math.floor(took / MS_PER_DAY)) : 0;
}

function isDailyLog(row: ConsoleRow): boolean {
  // Mobile observations share the log storage kind, but belong to the
  // Observations list. Keep chart membership aligned with matchesSavedView.
  return row.kind === "log" && row.recordType !== "observation";
}

/**
 * Is this row in the age band the chart is filtered to?
 *
 * Exported so the list and the chart agree by construction: both ask this,
 * neither re-derives the edges. A row the chart does not count — a daily log,
 * or anything already closed — is never in a band, because the bar is "open,
 * by age" and tapping it must not surface work that is done.
 */
export function inAgeBand(row: ConsoleRow, band: number, now: Date): boolean {
  if (isDailyLog(row) || TERMINAL.has(row.status)) return false;
  return bandIndexOf(ageDays(row, now)) === band;
}

export function buildItemFlow(rows: ConsoleRow[], now: Date): ItemFlow {
  // A daily log never appears in these charts for the same reason it never
  // stalls (`computeStall`, invariant 1): it is a record of what happened,
  // not work waiting on anybody, so an "open, by age" band would be counting
  // history as a backlog.
  const relevant = rows.filter((row) => !isDailyLog(row));
  const open = relevant.filter((row) => !TERMINAL.has(row.status));

  const bands = AGE_BANDS.map(([label]) => ({ label, count: 0 }));
  let oldestDays = 0;
  let overLineCount = 0;
  for (const row of open) {
    const days = ageDays(row, now);
    bands[bandIndexOf(days)]!.count += 1;
    if (days > oldestDays) oldestDays = days;
    if (days > AGING_CALLOUT_DAYS) overLineCount += 1;
  }

  const since = now.getTime() - CLOSE_WINDOW_DAYS * MS_PER_DAY;
  const closedRows = relevant.filter(
    (row) => TERMINAL.has(row.status) && Date.parse(row.lastMovementAt) >= since,
  );

  return {
    openCount: open.length,
    bands,
    oldestDays,
    overLine: { count: overLineCount, pct: pctOf(overLineCount, open.length) },
    closed: closedRows.length === 0 ? null : buildClosed(closedRows),
  };
}

function buildClosed(closedRows: ConsoleRow[]): ItemFlowClosed {
  const buckets = CLOSE_BUCKETS.map(([label]) => ({ label, count: 0 }));
  const days: number[] = [];
  /** owner → their close times, for the fastest-owner figure. */
  const byOwner = new Map<string, number[]>();

  for (const row of closedRows) {
    const took = closeDaysOf(row);
    days.push(took);
    buckets[bucketIndexOf(took)]!.count += 1;
    const owner = row.assigneeName?.trim();
    if (owner) byOwner.set(owner, [...(byOwner.get(owner) ?? []), took]);
  }

  days.sort((a, b) => a - b);
  return {
    count: closedRows.length,
    buckets,
    medianDays: medianOf(days),
    withinWeekPct: pctOf(days.filter((d) => d <= 7).length, days.length),
    fastest: fastestOwner(byOwner),
  };
}

/**
 * The owner who closes fastest, by MEDIAN rather than by best single close.
 *
 * A minimum would hand the title to whoever once closed something in an hour,
 * which is a lottery rather than a record. Ties break on the name so the
 * figure does not flicker between two owners across reloads.
 */
function fastestOwner(byOwner: Map<string, number[]>): { owner: string; days: number } | null {
  let best: { owner: string; days: number } | null = null;
  for (const [owner, times] of byOwner) {
    const median = medianOf([...times].sort((a, b) => a - b));
    if (best === null || median < best.days || (median === best.days && owner < best.owner)) {
      best = { owner, days: median };
    }
  }
  return best;
}

/** The kinds the charts are offered on — the three lists that are open WORK.
 *  Logs and photos are records; see `buildItemFlow`. */
export const FLOW_KINDS: ReadonlySet<ConsoleItemKind> = new Set<ConsoleItemKind>([
  "observation",
  "task",
  "punch",
]);
